/**
 * Installation Details List with Infinite Scroll
 *
 * Features:
 * - Infinite scroll with pagination
 * - Filters: From/To Date, InCharge, AMC Month, AMC/Non AMC, Status, Client Name, Area, City, Org Type
 * - All dropdown options are dynamic
 * - Manual fetch with filters
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Button, Box, TextField, Typography, Autocomplete } from '@mui/material';
import { Search as SearchIcon, BrushCleaning, Trash2 } from 'lucide-react';
import DataTable from '../common/DataTable/index';
import Tooltip from '../../../utils/Tooltip';
import DatePicker from '../common/DatePicker/DatePicker';
import {
  FilterFieldSx,
  DropDownSX,
  CSS_PRIMARY,
  PRIMARY_COLOR_HOVER
} from '../../../constants/style';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import { MastersId } from '../../../constants/constant';
import { fetchAMC } from '../../../apis/AMC';
import dayjs from 'dayjs';
import { useResponsive, userScreenAccess, IsAdmins, formatDate } from '../../../utils';
import Search from '../common/Search';
import ExportExcel from '../common/ExportExcel';
import AddAmc from './AddAmc';
import CommonDialog from '../common/CommonDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { localStorageKeyName, amcTypeOptions } from '../../../constants/constant';

const headers = [
  {
    label: 'In Charge',
    key: 'userName',
    minWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Client Name',
    key: 'clientName',
    minWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Area',
    key: 'area',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'City',
    key: 'city',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Org Type',
    key: 'orgType',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Is Temprorary',
    key: 'isTemporary',
    minWidth: 120,
    align: 'center',
    render: (val) => <span>{val ? 'Yes' : 'No'}</span>
  },
  {
    label: 'Bill No.',
    key: 'billNo',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Bill Date',
    key: 'paidDateString',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'AMC Bill Amount',
    key: 'paidAmount',
    minWidth: 120,
    sortable: true,
    resizable: true
  },

  {
    label: 'Start Date',
    key: 'amcStartDateString',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'End Date',
    key: 'amcEndDateString',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Remarks',
    key: 'remarks',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 120,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            _editRowHandler?.(row);
          }}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            borderRadius: 1.5,
            py: 0.3,
            px: 1.2,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
          }}
        >
          Edit
        </Button>

        {/* <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            _deleteRowHandler?.(row);
          }}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            borderRadius: 1.5,
            py: 0.3,
            px: 1.2,
            minWidth: 0
          }}
        >
          <Trash2 size={13} />
        </Button> */}
      </Box>
    )
  }
];

let _editRowHandler = null;
let _deleteRowHandler = null;

const initialFiltersValue = {
  dateFrom: formatDate(dayjs()),
  dateTo: formatDate(dayjs()),
  userGUID: 0
};

const AmcList = ({ pageId = 0, installationId = 0 }) => {
  // Filters state (for UI inputs)
  const [filters, setFilters] = useState(initialFiltersValue);

  const [inChargeOptions, setInChargeOptions] = useState([]);
  const [amcList, setAmcList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openAddAmcDialog, setOpenAddAmcDialog] = useState(false);
  const [selectedAMC, setSelectedAMC] = useState(null);
  const [selectedAmcType, setSelectedAmcType] = useState(amcTypeOptions[1]);
  const isAdmin = IsAdmins();

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const user = localStorage.getItem(localStorageKeyName);
  const userDetails = user ? JSON.parse(user) : null;

  const { isMobile } = useResponsive();
  const addInstallationScreen = pageId === MastersId.INSTALLATION_DETAILS;

  const userAccess = userScreenAccess();

  const filteredAmcList = useMemo(() => {
    if (!selectedAmcType || selectedAmcType.value === 'all') {
      return amcList;
    }
    if (selectedAmcType.value === 'temp') {
      return amcList.filter((item) => item.isTemporary === true);
    }
    if (selectedAmcType.value === 'annual') {
      return amcList.filter((item) => item.isTemporary === false);
    }
    return amcList;
  }, [amcList, selectedAmcType]);

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filteredAmcList;
    return filteredAmcList.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        if (key === 'isTemporary')
          return typeof val === 'boolean' ? (val ? 'yes' : 'no') : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [filteredAmcList, searchQuery]);

  // Calculate totals
  const totalBillAmount = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.paidAmount || 0), 0);
  }, [filteredData]);

  // Wire module-level handlers for the Actions column
  _editRowHandler = (row) => {
    if (!userAccess?.hasEditPermission) return;
    setSelectedAMC(row);
    setOpenAddAmcDialog(true);
  };

  _deleteRowHandler = (row) => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete AMC Record',
      content: `Are you sure you want to delete the AMC record for "${row.clientName || 'this client'
        }"? This action cannot be undone.`,
      onConfirm: () => {
        // 🔁 Replace the body below with your actual delete API call
        console.log('Deleting AMC row:', row);
        // e.g. await deleteAMC(row.amcGUID);
        closeConfirm();
        handleFetch(); // refresh list after delete
      }
    });
  };

  const handleCloseAddAmcDialog = () => {
    setOpenAddAmcDialog(false);
    setSelectedAMC(null);
  };

  // API filters state (only updated when Fetch button is clicked)
  const [apiFilters, setApiFilters] = useState({
    fromDate: null,
    toDate: null,
    inCharge: ''
  });

  // Reset trigger - change this to reset infinite scroll
  const [filterResetKey, setFilterResetKey] = useState(0);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateFrom: nextRange?.startDate ? dayjs(nextRange.startDate) : null,
      dateTo: nextRange?.endDate ? dayjs(nextRange.endDate) : null
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFetch();
    }
  };

  const handleAmcTypeChange = (event, value) => {
    setSelectedAmcType(value || amcTypeOptions[0]);
  };

  const fetchDependencies = async () => {
    try {
      const [usersRes] = await Promise.all([fetchUsersCombo()]);

      const loadedUsers = usersRes || [];

      setInChargeOptions(loadedUsers);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const fetchAmcListResponse = async (filters) => {
    setIsLoading(true);
    try {
      const response = await fetchAMC(filters);
      setAmcList(response || []);
    } catch (err) {
      console.log('Error fetching installation list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch dependencies on mount
  React.useEffect(() => {
    const payload = {
      ...initialFiltersValue,
      userGUID: userDetails.userGUID
    };
    setFilters(payload);
    fetchDependencies();
    fetchAmcListResponse(payload);
  }, []);

  /**
   * NEW: Reset infinite scroll when filters change
   * This forces the DataTable to reinitialize with new filters
   */
  const handleResetFilters = () => {
    const payload = {
      ...initialFiltersValue,
      userGUID: userDetails.userGUID
    };
    setFilters(payload);
    fetchAmcListResponse(payload);
  };

  const handleFetch = () => {
    // setApiFilters({ ...filters }); // Copy current UI filters to API filters
    // setFilterResetKey((prev) => prev + 1); // Trigger DataTable reset and fetch
    const payload = {
      ...filters,
      dateFrom: formatDate(filters.dateFrom),
      dateTo: formatDate(filters.dateTo)
    };
    fetchAmcListResponse(payload);
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...filters, userGUID };
    setFilters(next);
  };

  const handleStartDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateFrom: nextRange || null
    });
  };

  const handleEndDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateTo: nextRange || null
    });
  };

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          AMC List
        </Typography>
      </Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'divider',
          display: addInstallationScreen ? 'none' : ''
        }}
      >
        {/* Filter Fields Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 1,
            mb: 2,
            width: '100%',
            alignItems: 'center'
          }}
        >
          {/* Date Range Filter */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2' }
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                minWidth: 0
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  placeholder="Start Date"
                  label="Start Date"
                  value={filters.dateFrom || null}
                  onChange={handleStartDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  label="End Date"
                  placeholder="End Date"
                  value={filters.dateTo || null}
                  onChange={handleEndDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>
            </Box>
          </Box>

          <Autocomplete
            size="small"
            fullWidth
            options={inChargeOptions}
            value={inChargeOptions.find((u) => u.userGUID === filters.userGUID) || null}
            onChange={handleInChargeChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.userName || ''}
            isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
            readOnly={!isAdmin}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="In Charge"
                placeholder="Select In Charge"
                sx={FilterFieldSx}
              />
            )}
          />

          <Autocomplete
            size="small"
            options={amcTypeOptions}
            value={selectedAmcType}
            onChange={handleAmcTypeChange}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            disablePortal={false}
            sx={{ width: { xs: '100%', sm: '250px' } }}
            slotProps={{
              paper: {
                sx: { width: '100%', minWidth: '250px' }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="AMC Type"
                placeholder="Select AMC Type"
                sx={FilterFieldSx}
              />
            )}
          />

          {/* Fetch Button */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tooltip text="Fetch">
              <Button
                variant="outlined"
                size="small"
                onClick={handleFetch}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  textCapitalize: 'capitalize',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  py: 0.4,
                  px: 1.2,
                  color: CSS_PRIMARY, // text color
                  borderColor: CSS_PRIMARY, // outline color

                  '&:hover': {
                    borderColor: PRIMARY_COLOR_HOVER,
                    color: PRIMARY_COLOR_HOVER
                  }
                }}
              >
                <SearchIcon size={14} style={{ marginRight: 4 }} />
                Fetch
              </Button>
            </Tooltip>

            {/* Reset Button */}
            <Tooltip text="Reset filters">
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetFilters}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  py: 0.4,
                  px: 1.2,
                  color: CSS_PRIMARY, // text color
                  borderColor: CSS_PRIMARY, // outline color

                  '&:hover': {
                    borderColor: PRIMARY_COLOR_HOVER,
                    color: PRIMARY_COLOR_HOVER
                  }
                }}
              >
                <BrushCleaning size={14} style={{ marginRight: 4 }} />
                Reset
              </Button>
            </Tooltip>
          </div>
        </Box>
      </Box>

      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by AMC..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className={`d-flex align-items-center gap-3 flex-shrink-0`}>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'capitalize',
              backgroundColor: CSS_PRIMARY,
              '&:hover': {
                backgroundColor: PRIMARY_COLOR_HOVER
              }
            }}
          >
            Total Count: {filteredData.length}
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'capitalize',
              backgroundColor: CSS_PRIMARY,
              '&:hover': {
                backgroundColor: PRIMARY_COLOR_HOVER
              }
            }}
          >
            Total Bill AMT: ₹{totalBillAmount.toLocaleString()}
          </Button>
        </div>

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={amcList}
            fileName={`AmcList_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName={'AmcList'}
            buttonLabel="Excel"
            exportKeys={headers.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={headers.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
        </div>
      </div>

      {/* <DataTable
        key={filterResetKey}
        headers={headers}
        data={[]} // Empty initially; API will populate
        enableInfiniteScroll={true}
        apiFunction={fetchInstallationListWithPagination}
        pageSize={50}
        scrollHeight="400px"
        enablePagination={false}
        emptyMessage="No installation details found. Try adjusting your filters."
      /> */}
      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={'No AMCs found in the system.'}
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      <CommonDialog
        open={openAddAmcDialog}
        onClose={handleCloseAddAmcDialog}
        title={`${selectedAMC ? 'Edit' : 'Add'} AMC`}
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddAmc
          installationId={selectedAMC?.installationGUID}
          amcDetails={selectedAMC}
          userAccess={userAccess}
          onSuccess={() => {
            handleCloseAddAmcDialog();
            handleFetch();
          }}
        />
      </CommonDialog>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash2 size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </div>
  );
};

export default AmcList;
